import axios from 'axios';
import { Config } from '../config/envConfig';
import { getUrl } from '../components/pdfDisplay/utils';

// Full URL for the refresh-token endpoint (matches the domain cookies are scoped to)
const REFRESH_TOKEN_URL = () =>
    getUrl(
        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/refresh-token`
    );

// Mutex and queue for token refresh
let isRefreshing = false;
let failedQueue = [];

// Proactive token refresh
let refreshTimer = null;
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000; // 25 minutes (refresh before 30-min expiration)

/**
 * Process all queued requests after refresh completes
 */
const processQueue = (error) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });

    failedQueue = [];
};

/**
 * Proactively refresh token before expiration
 */
const refreshTokenProactively = async () => {
    // Don't refresh if already refreshing
    if (isRefreshing) {
        return;
    }

    // Check if user is logged in
    const currentUser = localStorage.getItem('current');
    if (!currentUser) {
        stopProactiveRefresh();
        return;
    }

    try {
        isRefreshing = true;
        await axios.post(
            REFRESH_TOKEN_URL(),
            {},
            {
                withCredentials: true,
            }
        );
        console.log('🔄 Token refreshed proactively');
        isRefreshing = false;
    } catch (error) {
        console.error('❌ Proactive token refresh failed:', error);
        isRefreshing = false;

        // If refresh fails, stop the timer and let the user re-login
        stopProactiveRefresh();

        // Only redirect if it's a critical error (not network issues)
        if (error.response?.status === 401) {
            localStorage.removeItem('current');
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login';
            }
        }
    }
};

/**
 * Start proactive token refresh timer
 */
const startProactiveRefresh = () => {
    // Clear any existing timer
    stopProactiveRefresh();

    // Start new timer
    refreshTimer = setInterval(refreshTokenProactively, TOKEN_REFRESH_INTERVAL);
    console.log('✅ Proactive token refresh started (every 25 minutes)');
};

/**
 * Stop proactive token refresh timer
 */
const stopProactiveRefresh = () => {
    if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
        console.log('🛑 Proactive token refresh stopped');
    }
};

/**
 * Initialize proactive refresh if user is logged in
 */
if (localStorage.getItem('current')) {
    startProactiveRefresh();
}

/**
 * Response interceptor for handling 401 errors and token refresh
 */
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Check if error is 401 and not already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
            const code = error.response?.data?.code;

            // ── Security violations: redirect immediately, no refresh attempt ──
            if (code === 'TOKEN_REVOKED') {
                localStorage.removeItem('current');
                stopProactiveRefresh();
                if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                    alert('Your session has expired. Please login again.');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }

            if (code === 'TOKEN_REUSE') {
                console.error('🚨 Token reuse detected, all sessions revoked');
                localStorage.removeItem('current');
                stopProactiveRefresh();
                if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                    alert('Security alert: Token reuse detected. Please login again.');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }

            // ── Refreshable codes: try refresh before giving up ──
            // TOKEN_EXPIRED  – JWT/DB expiry
            // NO_TOKEN       – access_token cookie gone (e.g. idle > 30 min, browser cleared it)
            // TOKEN_INVALIDATED – stale token in header after rotation; cookie will be fresh after refresh
            // INVALID_TOKEN  – bad signature (e.g. SECRET_KEY rotated after restart)
            const isRefreshableCode =
                code === 'TOKEN_EXPIRED' ||
                code === 'NO_TOKEN' ||
                code === 'TOKEN_INVALIDATED' ||
                code === 'INVALID_TOKEN';

            if (isRefreshableCode) {
                // Avoid infinite loop: don't try to refresh the refresh endpoint itself
                if (originalRequest.url?.includes('refresh-token')) {
                    return Promise.reject(error);
                }

                // If already refreshing, queue this request
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then(() => {
                            // Clear stale Authorization header before retry
                            delete originalRequest.headers['authorization'];
                            return axios(originalRequest);
                        })
                        .catch((err) => Promise.reject(err));
                }

                // Mark as retrying to prevent infinite loop
                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    await axios.post(
                        REFRESH_TOKEN_URL(),
                        {},
                        { withCredentials: true }
                    );

                    // Refresh succeeded – process queue and restart proactive timer
                    processQueue(null);
                    isRefreshing = false;
                    startProactiveRefresh();

                    // Clear stale Authorization header and retry original request
                    delete originalRequest.headers['authorization'];
                    return axios(originalRequest);
                } catch (refreshError) {
                    // Capture before clearing — unauthenticated users on public pages
                    // should not be force-redirected to /login on failed API calls.
                    const wasAuthenticated = !!localStorage.getItem('current');
                    processQueue(refreshError);
                    isRefreshing = false;
                    localStorage.removeItem('current');
                    stopProactiveRefresh();

                    // TOKEN_REVOKED / TOKEN_REUSE from the refresh endpoint are already
                    // handled with an alert by this same interceptor (nested call above).
                    // For all other refresh failures, show a session-expired message.
                    const refreshCode = refreshError.response?.data?.code;
                    const alreadyHandled =
                        refreshCode === 'TOKEN_REVOKED' ||
                        refreshCode === 'TOKEN_REUSE';

                    if (
                        !alreadyHandled &&
                        wasAuthenticated &&
                        window.location.pathname !== '/login' &&
                        window.location.pathname !== '/' &&
                        refreshError.response
                    ) {
                        alert('Your session has expired. Please login again.');
                        window.location.href = '/login';
                    } else if (
                        !alreadyHandled &&
                        window.location.pathname !== '/login' &&
                        !refreshError.response
                    ) {
                        // Network error during refresh – don't redirect, just let request fail
                        console.error(
                            '❌ Token refresh failed due to network error'
                        );
                    }

                    return Promise.reject(refreshError);
                }
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Request interceptor to ensure credentials are sent only to Node.js server
 */
axios.interceptors.request.use(
    (config) => {
        // Only send credentials (cookies) to Node.js server
        // FastAPI server doesn't need credentials and uses wildcard CORS
        const url = config.url || '';
        const isNodeServer =
            url.includes(`:${Config.NODE_SERVER_PORT}`) ||
            (!url.includes(`:${Config.FAST_API_PORT}`) &&
                !url.includes('http'));

        if (isNodeServer) {
            config.withCredentials = true;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * When the tab becomes visible again, immediately refresh the token.
 * This handles browser timer throttling in background/inactive tabs,
 * which can prevent the 25-minute setInterval from firing on time.
 */
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (
            document.visibilityState === 'visible' &&
            localStorage.getItem('current')
        ) {
            refreshTokenProactively();
        }
    });
}

// Export utility functions for manual control if needed
export { startProactiveRefresh, stopProactiveRefresh };

export default axios;
